// @flow

import memoize from 'memoize-weak';
import { get } from 'lodash';

import ServiceUtils from 'app/service/ServiceUtils';

import type { Service } from 'app/service/types';

/**
 * These are helper functions to transform the response to match the current
 * `/api/v3/services` response. This allows us to have the least amount of
 * changes to the editor while retrieving the data in a very different format.
 * In the future, we will need to modify core parts of the editor and
 * ServiceUtils to align with the GraphQL response.
 */

const getActionsByType = (type, service) => {
  const actions = get(service, ['actions', type], []);

  return actions.map(action => ({
    ...action,
    help_text: action.helpText,
    help_text_html: action.helpTextHtml,
    hook: action.isHook,
    hook_directions_html: action.hookDirectionsHtml,
    hook_legacy_url: action.hookLegacyUrl,
    hook_notification: action.isHookNotification,
    hook_pollable: action.isHookPollable,
    hook_subscription: action.isHookSubscription,
    hook_to_poll: action.isHookToPoll,
    hook_url: action.hookUrl,
    write_delegate: action.writeDelegate,
    search_delegate: action.searchDelegate,
  }));
};

const getServiceSearch = memoize((service: Service) => {
  const searchActions = getActionsByType('searches', service);
  const searchOrWriteActions = getActionsByType('searchesOrWrites', service);

  const serviceSearch = !service.hasSearchesOrWrites
    ? searchActions
    : searchActions.map(search => {
        search.hasCreate = searchOrWriteActions.some(searchOrWrite => {
          return searchOrWrite.searchDelegate === search.key;
        });

        return search;
      });
  return serviceSearch;
});

/**
 * This function is a temporary solution to transform the GraphQL response to
 * match the legacy services response. Eventually we should conform to the
 * properties provided by the GraphQL endpoint.
 */
const toLegacyCompatibleService: (service: Service) => Service = memoize(
  service => {
    const compatible = {
      key: service.id,
      hasSearchOrWrites: service.hasSearchesOrWrites, // TODO(neilff): var names should be the same
      builtIn: ServiceUtils.isBuiltIn(service),
      featured: ServiceUtils.isFeatured(service),
      hidden: ServiceUtils.isHidden(service),
      inviteApp: ServiceUtils.isInvite(service),
      privateApp: !ServiceUtils.isPublic(service),
      title: service.name,
      // Assume low popularity app if it does not have a value
      order: service.popularity || 9999,
      // Rather than eagerly compute the values of these properties
      // use the getter syntax to lazily compute as-needed
      get search() {
        return getServiceSearch(service);
      },
      get search_or_write() {
        return getActionsByType('searchesOrWrites', service);
      },
      get filter() {
        return getActionsByType('filters', service);
      },
      get read() {
        return getActionsByType('reads', service);
      },
      get write() {
        return getActionsByType('writes', service);
      },
    };

    return { ...service, ...compatible };
  }
);

export default toLegacyCompatibleService;
